import { getExpressAPIHost } from '../utils';

/**
 * In-flight promise deduplication.
 * When multiple callers invoke fetchRegionFromBackend concurrently (App.js, Login,
 * getUserRegion, getUser), they share one fetch() call instead of each firing their own.
 * This prevents thundering herd when Express is slow or processing multiple startup requests.
 */
let inflightPromise = null;

/**
 * Fetches the region configuration from the backend API
 * This is used to determine the correct coreSystem for authentication
 * in dev/staging environments where the URL doesn't match the actual region.
 *
 * The region is cached in sessionStorage to avoid repeated API calls.
 * Concurrent callers share one in-flight request via the inflightPromise pattern.
 *
 * @returns {Promise<string>} The region code (e.g., 'UDA', 'MMPA', etc.)
 */
export const fetchRegionFromBackend = async () => {
    const cachedRegion = sessionStorage.getItem('mm.region');
    if (cachedRegion) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[RegionConfig] Using cached region:', cachedRegion);
        }
        return cachedRegion;
    }

    if (inflightPromise) {
        if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[RegionConfig] Joining in-flight region fetch');
        }
        return inflightPromise;
    }

    inflightPromise = (async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000); // 8s timeout

        try {
            const apiHost = getExpressAPIHost();
            const response = await fetch(`${apiHost}/region-config`, { signal: controller.signal });

            if (!response.ok) {
                throw new Error(`Failed to fetch region config: ${response.status}`);
            }

            const data = await response.json();
            let region = data?.region;

            if (typeof region !== 'string') {
                throw new Error('Region not found in response');
            }

            region = region.trim().toUpperCase();
            if (!region) {
                throw new Error('Region not found in response');
            }

            sessionStorage.setItem('mm.region', region);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('[RegionConfig] Fetched and cached region:', region);
            }
            return region;
        } catch (error) {
            // eslint-disable-next-line no-console
            if (process.env.NODE_ENV !== 'production') {
                console.error('[RegionConfig] Error fetching region from backend:', error);
            }
            const { getRegion } = await import('../utils');
            const fallbackRegion = getRegion(window.location.hostname);
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('[RegionConfig] Using fallback region from URL:', fallbackRegion);
            }
            return fallbackRegion;
        } finally {
            clearTimeout(timeoutId);
        }
    })().finally(() => {
        inflightPromise = null;
    });

    return inflightPromise;
};

/**
 * Clears the cached region from sessionStorage
 * This should be called on logout to ensure fresh region fetch on next login
 */
export const clearCachedRegion = () => {
    sessionStorage.removeItem('mm.region');
    // eslint-disable-next-line no-console
    console.log('[RegionConfig] Cleared cached region');
};
